class StockistFinder {
    constructor() {
        this.stockists = [];
        this.map = null;
        this.markers = [];
        this.userLocation = null;
        console.log('StockistFinder initialized');
        this.loadStockists();
        this.initGoogleMaps();
    }

    async logAction(action, result) {
        const now = new Date();
        const time = now.toLocaleTimeString('en-GB');
        const date = now.toLocaleDateString('en-GB');
        
        const formData = new URLSearchParams();
        formData.append('entry.647705854', time);    // Time field
        formData.append('entry.1156734951', date);   // Date field
        formData.append('entry.448709161', action);  // Action field
        formData.append('entry.1279622698', result); // Result field

        try {
            const response = await fetch('https://docs.google.com/forms/d/e/1FAIpQLScgP5UpgDWgPHUj1r8oQUz8JZ72oAFP8mEaDvErlGPRsXn5jw/formResponse', {
                method: 'POST',
                mode: 'no-cors',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: formData.toString()
            });
            console.log('Action logged:', { time, date, action, result });
        } catch (error) {
            console.error('Error logging action:', error);
        }
    }

    async initGoogleMaps() {
        console.log('Initializing Google Maps');
        // Load Google Maps JavaScript API
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyB5jUtYhzt7FHrxwWE5wl1chY175Qtgqzk&libraries=places`;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
        
        return new Promise(resolve => {
            script.onload = () => {
                console.log('Google Maps loaded');
                // Initialize map
                this.map = new google.maps.Map(document.getElementById('map'), {
                    zoom: 6,
                    center: { lat: 54.5, lng: -4.0 }, // Center of UK
                    mapTypeControl: false,
                    streetViewControl: false
                });
                resolve();
            };
        });
    }

    async loadStockists() {
        try {
            console.log('Loading stockists data...');
            const response = await fetch(window.STOCKISTS_DATA_URL);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const text = await response.text();
            console.log('Raw response:', text);

            // Remove Google Sheets prefix and suffix
            const jsonText = text.substring(text.indexOf('{'), text.lastIndexOf('}') + 1);
            console.log('Cleaned JSON:', jsonText);
            
            const data = JSON.parse(jsonText);
            console.log('Parsed data:', data);
            
            // Transform Google Sheets data into our stockists format
            this.stockists = data.table.rows.map(row => {
                const cols = row.c;
                if (!cols) return null; // Skip empty rows
                
                // Only process rows that have all required fields
                const stockist = {
                    title: cols[0]?.v || '',
                    address: cols[1]?.v || '',
                    postcode: cols[2]?.v || '',
                    phone: cols[3]?.v || '',
                    latitude: parseFloat(cols[4]?.v) || 0,
                    longitude: parseFloat(cols[5]?.v) || 0
                };
                
                // Validate required fields
                if (!stockist.title || !stockist.latitude || !stockist.longitude) {
                    console.log('Skipping invalid stockist:', stockist);
                    return null;
                }
                
                return stockist;
            })
            .filter(stockist => stockist !== null); // Remove invalid entries
            
            console.log('Processed stockists:', this.stockists);
            
            if (this.stockists.length === 0) {
                throw new Error('No valid stockists found in the data');
            }
        } catch (error) {
            console.error('Error loading stockists:', error);
            this.stockists = [];
            alert('Unable to load stockists data. Please try again later.');
        }
    }

    calculateDistance(lat1, lon1, lat2, lon2) {
        const R = 6371; // Earth's radius in km
        const dLat = this.toRad(lat2 - lat1);
        const dLon = this.toRad(lon2 - lon1);
        const a = 
            Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) * 
            Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        return R * c;
    }

    toRad(value) {
        return value * Math.PI / 180;
    }

    clearMarkers() {
        this.markers.forEach(marker => marker.setMap(null));
        this.markers = [];
    }

    addMarker(position, title, isUser = false) {
        const marker = new google.maps.Marker({
            position,
            map: this.map,
            title,
            icon: isUser ? null : {
                url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png'
            }
        });
        this.markers.push(marker);
        return marker;
    }

    async getCoordinatesFromPostcode(postcode) {
        console.log('Getting coordinates for postcode:', postcode);
        const geocoder = new google.maps.Geocoder();
        
        return new Promise((resolve, reject) => {
            geocoder.geocode({ 
                address: postcode + ', UK' // Add UK to improve accuracy
            }, (results, status) => {
                console.log('Geocoding results:', results, 'status:', status);
                if (status === 'OK') {
                    const location = results[0].geometry.location;
                    this.userLocation = {  // Store user's location when geocoding
                        lat: location.lat(),
                        lng: location.lng()
                    };
                    resolve(this.userLocation);
                } else {
                    reject(new Error('Could not find location for this postcode'));
                }
            });
        });
    }

    getDirectionsUrl(stockist) {
        const origin = this.userLocation 
            ? `${this.userLocation.lat},${this.userLocation.lng}`
            : document.getElementById('postcode-input').value;
        
        const destination = `${stockist.latitude},${stockist.longitude}`;
        return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}`;
    }

    async findNearestStockists(postcode) {
        try {
            console.log('Finding nearest stockists for:', postcode);
            const coordinates = await this.getCoordinatesFromPostcode(postcode);
            
            if (!coordinates) {
                throw new Error('Invalid postcode');
            }

            console.log('Got coordinates:', coordinates);
            console.log('Current stockists:', this.stockists);

            // Clear existing markers
            this.clearMarkers();

            // Add user location marker
            this.addMarker(coordinates, 'Your Location', true);

            // Calculate distances and sort stockists
            this.stockists.forEach(stockist => {
                stockist.distance = this.calculateDistance(
                    coordinates.lat,
                    coordinates.lng,
                    stockist.latitude,
                    stockist.longitude
                ) * 0.621371; // Convert km to miles
            });

            // Sort by distance and get top 6
            const nearestStockists = this.stockists
                .sort((a, b) => a.distance - b.distance)
                .slice(0, 6);

            // Add markers for nearest stockists
            nearestStockists.forEach(stockist => {
                this.addMarker(
                    { lat: stockist.latitude, lng: stockist.longitude },
                    stockist.title
                );
            });

            // Fit map bounds to include all markers
            const bounds = new google.maps.LatLngBounds();
            this.markers.forEach(marker => bounds.extend(marker.getPosition()));
            this.map.fitBounds(bounds);

            // If only one marker, zoom out a bit
            if (this.markers.length === 1) {
                this.map.setZoom(12);
            }

            return nearestStockists;
        } catch (error) {
            console.error('Error finding stockists:', error);
            throw error;
        }
    }

    async findNearestStockistsByCoordinates(coordinates) {
        try {
            console.log('Finding nearest stockists by coordinates:', coordinates);
            
            if (!coordinates || !coordinates.lat || !coordinates.lng) {
                throw new Error('Invalid coordinates');
            }

            console.log('Using coordinates:', coordinates);
            console.log('Current stockists:', this.stockists);

            // Clear existing markers
            this.clearMarkers();

            // Add user location marker
            this.addMarker(coordinates, 'Your Location', true);

            // Calculate distances and sort stockists
            this.stockists.forEach(stockist => {
                stockist.distance = this.calculateDistance(
                    coordinates.lat,
                    coordinates.lng,
                    stockist.latitude,
                    stockist.longitude
                ) * 0.621371; // Convert km to miles
            });

            // Sort by distance and get top 6
            const nearestStockists = this.stockists
                .sort((a, b) => a.distance - b.distance)
                .slice(0, 6);

            // Add markers for nearest stockists
            nearestStockists.forEach(stockist => {
                this.addMarker(
                    { lat: stockist.latitude, lng: stockist.longitude },
                    stockist.title
                );
            });

            // Fit map bounds to include all markers
            const bounds = new google.maps.LatLngBounds();
            this.markers.forEach(marker => bounds.extend(marker.getPosition()));
            this.map.fitBounds(bounds);

            // If only one marker, zoom out a bit
            if (this.markers.length === 1) {
                this.map.setZoom(12);
            }

            return nearestStockists;
        } catch (error) {
            console.error('Error finding stockists by coordinates:', error);
            throw error;
        }
    }

    async logAction(action, data) {
        try {
            console.log('Logging action:', action, 'data:', data);
            const response = await fetch(window.LOGGING_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action,
                    data
                })
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
        } catch (error) {
            console.error('Error logging action:', error);
        }
    }
}

// Initialize the finder when the DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
    console.log('DOM loaded, initializing StockistFinder');
    const finder = new StockistFinder();
    
    const form = document.getElementById('stockist-finder-form');
    const resultsContainer = document.getElementById('stockist-results');
    const useLocationBtn = document.getElementById('use-location-btn');
    const postcodeInput = document.getElementById('postcode-input');

    if (!form || !resultsContainer) {
        console.error('Could not find form or results container');
        return;
    }

    // Check if geolocation is supported
    if (!navigator.geolocation) {
        if (useLocationBtn) {
            useLocationBtn.disabled = true;
            useLocationBtn.textContent = 'Location not supported';
        }
    }

    // Use My Location button handler
    if (useLocationBtn) {
        useLocationBtn.addEventListener('click', async () => {
            console.log('Use location button clicked');
            useLocationBtn.disabled = true;
            useLocationBtn.textContent = '📍 Getting location...';
            
            try {
                const position = await getCurrentPosition();
                const coordinates = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                };
                
                console.log('Got user location:', coordinates);
                finder.userLocation = coordinates;
                
                // Log the location search action
                finder.logAction('LOCATION SEARCH', `${coordinates.lat},${coordinates.lng}`);
                
                // Clear postcode input since we're using location
                postcodeInput.value = '';
                postcodeInput.required = false;
                
                // Find nearest stockists using coordinates
                const nearestStockists = await finder.findNearestStockistsByCoordinates(coordinates);
                console.log('Found nearest stockists by location:', nearestStockists);
                displayResults(nearestStockists, resultsContainer, finder);
                
                useLocationBtn.textContent = '📍 Location used';
                
            } catch (error) {
                console.error('Location error:', error);
                let errorMessage = 'Could not get your location';
                
                if (error.code === 1) {
                    errorMessage = 'Location access denied. Please enable location services.';
                } else if (error.code === 2) {
                    errorMessage = 'Location unavailable. Please try again.';
                } else if (error.code === 3) {
                    errorMessage = 'Location request timed out. Please try again.';
                }
                
                resultsContainer.innerHTML = `<p class="error">${errorMessage}</p>`;
                useLocationBtn.textContent = '📍 Use My Location';
            } finally {
                useLocationBtn.disabled = false;
                postcodeInput.required = true;
            }
        });
    }

    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        console.log('Form submitted');
        const postcode = document.getElementById('postcode-input').value.toUpperCase();
        
        // Log the search action
        finder.logAction('POSTCODE SEARCH', postcode);
        
        try {
            const nearestStockists = await finder.findNearestStockists(postcode);
            console.log('Found nearest stockists:', nearestStockists);
            displayResults(nearestStockists, resultsContainer, finder);
        } catch (error) {
            console.error('Error:', error);
            resultsContainer.innerHTML = `<p class="error">Error: ${error.message}</p>`;
        }
    });
});

// Helper function to promisify geolocation
function getCurrentPosition() {
    return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
            resolve,
            reject,
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 300000 // 5 minutes
            }
        );
    });
}

function displayResults(stockists, container, finder) {
    console.log('Displaying results:', stockists);
    if (!stockists.length) {
        container.innerHTML = '<p>No stockists found in your area.</p>';
        return;
    }

    let html = '<div class="stockist-results-grid">';
    stockists.forEach((stockist, index) => {
        const cleanPhone = stockist.phone ? stockist.phone.replace(/[^\d+]/g, '') : '';
        
        html += `
            <div class="stockist-card">
                <div class="stockist-info">
                    <h3>${stockist.title}</h3>
                    <p class="address">${stockist.address}</p>
                    <p class="postcode">${stockist.postcode}</p>
                    ${stockist.phone ? `
                        <p class="phone">
                            <a href="tel:${cleanPhone}" class="phone-link" onclick="window.stockistFinder.logAction('CALL', '${stockist.title}')">
                                📞 ${stockist.phone}
                            </a>
                        </p>
                    ` : ''}
                    <p class="distance">📍 ${stockist.distance.toFixed(1)} miles away</p>
                    <a href="${finder.getDirectionsUrl(stockist)}" 
                       class="directions-button" 
                       target="_blank" 
                       rel="noopener"
                       onclick="window.stockistFinder.logAction('DIRECTIONS', '${stockist.title}'); return true;">
                        <span class="directions-icon">🗺️</span> Get Directions
                    </a>
                </div>
                <div class="stockist-map" id="map-${index}"></div>
            </div>
        `;
    });
    html += '</div>';
    container.innerHTML = html;

    // Make the finder instance available globally for the onclick handlers
    window.stockistFinder = finder;

    // Initialize small maps after the elements are added to DOM
    stockists.forEach((stockist, index) => {
        const mapElement = document.getElementById(`map-${index}`);
        if (mapElement) {
            const smallMap = new google.maps.Map(mapElement, {
                zoom: 15,
                center: { lat: stockist.latitude, lng: stockist.longitude },
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
                zoomControl: false
            });
            new google.maps.Marker({
                position: { lat: stockist.latitude, lng: stockist.longitude },
                map: smallMap,
                title: stockist.title
            });
        }
    });
}
